import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import { FormControl, InputAdornment, FormHelperText, Button } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { MdOutlineCloudUpload } from "react-icons/md";
import DropZonefile from "components/dropZone/DropZoneFile";
import useAuth from "hooks/useAuth";
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input'
import { CNPJMask } from "components/CNPJInput/CNPJInput";
import Grid from '@mui/material/Unstable_Grid2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import 'dayjs/locale/pt-br';


dayjs.locale('pt-br');

const getNestedValue = (obj, path) => {
    return path.split('.').reduce((o, p) => (o ? o[p] : null), obj);
};

// Helper function to convert decimal to percentage
const decimalToPercentage = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return (parseFloat(value) * 100).toFixed(2);
};

// Helper function to convert percentage to decimal with proper rounding
const percentageToDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '';
    // Fix precision issues by rounding to 4 decimal places
    return parseFloat((parseFloat(value) / 100).toFixed(4));
};

const DynamicFormField = ({ field, formData = {} }) => {
    const mdColSpan = field.mdColSpan || 12;
    const { auth, authLoading } = useAuth();
    const [isVisible, setIsVisible] = useState(true);

    // Add state to track percentage field editing state
    const [percentageState, setPercentageState] = useState({
        isEditing: false,
        editValue: ''
    });
    const inputRef = useRef(null);

    useEffect(() => {
        if (field.showIf) {
            setIsVisible(field.showIf(formData));
        }
    }, [formData, field]);

    // Initialize or update edit value when form data changes for percentage fields
    useEffect(() => {
        if (field.type === 'percentage' && !percentageState.isEditing) {
            const decimal = getNestedValue(formData, field.id);
            if (decimal !== null && decimal !== undefined && decimal !== '') {
                setPercentageState(prev => ({
                    ...prev,
                    editValue: (parseFloat(decimal) * 100).toFixed(2)
                }));
            }
        }
    }, [field.type, field.id, formData]);

    if (authLoading) return <div>Loading...</div>;

    if (!isVisible) {
        return null;
    }

    const userRoles = auth?.groups || [];

    if (field.visibleTo) {
        const showField = field.visibleTo.some(role => userRoles.includes(role));
        if (!showField) {
            return null;
        }
    }

    const disabledTo = field.disabledTo || [];

    const isDisabled = disabledTo.some(role => userRoles.includes(role)) || field.disabled;

    switch (field.type) {
        case "section":
            return (
                <Grid xs={12} md={12} key={field.id}>
                    <Box sx={{ my: 2, mb: 0 }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                            {field.title}
                        </Typography>
                        <Divider />
                    </Box>
                </Grid>
            );
        case "select":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <Autocomplete
                            key={field.id}
                            id={field.id}
                            name={field.id}
                            options={[{ label: '', value: null }, ...field.options]} // Add an empty option at the beginning of the options array
                            getOptionLabel={(option) => option.label}
                            defaultValue={field.options.find((option) => option.value === field.value || null)}
                            onChange={(event, newValue) => {
                                field.handleInputChange({ target: { id: field.id, name: field.id, value: newValue?.value || null } });
                            }}
                            renderInput={(params) => <TextField {...params} label={field.label} />}
                            value={field.options.find((option) => option.value === field.value) || null}
                            disabled={isDisabled} // Add the disabled prop here
                        />
                    </FormControl>
                </Grid>
            )
        case "multiselect":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <Autocomplete
                            multiple
                            key={field.id}
                            id={field.id}
                            name={field.id}
                            options={field.options}
                            getOptionLabel={(option) => option.label}
                            defaultValue={field.value || []}
                            onChange={(event, newValue) => {
                                // convert the array of options to an array of values
                                const newValues = newValue.map(option => option.value);

                                field.handleInputChange({ target: { id: field.id, name: field.id, value: newValues || null } });
                            }}
                            renderInput={(params) => <TextField {...params} label={field.label} />}
                            value={field.options.filter(option => (field.value || []).includes(option.value))}
                            disabled={isDisabled}
                        />
                    </FormControl>
                </Grid>
            );
        case "date":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <DatePicker
                                fullWidth
                                id={field.id}
                                label={field.label}
                                value={getNestedValue(formData, field.id) || "" ? dayjs(getNestedValue(formData, field.id) || "") : null}
                                onChange={(date) => {
                                    const formattedValue = date
                                        ? (field.dateFormat ? date.format(field.dateFormat) : date.format('YYYY-MM-DD'))
                                        : null;
                                    field.handleInputChange({
                                        target: {
                                            id: field.id,
                                            name: field.id,
                                            value: formattedValue
                                        }
                                    });
                                }}
                                disabled={isDisabled} // Add the disabled prop here
                            />
                        </LocalizationProvider>
                    </FormControl>
                </Grid>
            );
        case "textarea":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <TextField
                            key={field.id}
                            label={field.label}
                            placeholder={field.placeholder}
                            id={field.id}
                            name={field.id}
                            type={field.type}
                            value={getNestedValue(formData, field.id) || "" || ""}
                            onChange={field.handleInputChange}
                            disabled={isDisabled} // Add the disabled prop here
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            )
        case "switch":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <FormControlLabel control={<Switch
                            checked={getNestedValue(formData, field.id) || "" || false} // Add a conditional check to ensure getNestedValue(formData, field.id) || "" is defined
                            onChange={field.handleInputChange}
                            name={field.id}
                            inputProps={{ 'aria-label': 'controlled' }}
                            key={field.id}
                            id={field.id}
                            disabled={isDisabled} // Add the disabled prop here
                        />} label={field.label} />
                    </FormControl>
                </Grid>
            );
        case "dropzone":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <DropZonefile
                            content={
                                <div className="flex h-[225px] w-full flex-col items-center justify-center rounded-md border-[2px] border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-500 sm:w-[200px] md:w-[250px] lg:w-[300px] xl:w-[250px] 2xl:w-[300px] 3xl:w-[300px] p-2">
                                    <p className="text-[80px] text-navy-700">
                                        <MdOutlineCloudUpload className="text-brand-500 dark:text-white" />
                                    </p>
                                    <p className="text-lg font-bold text-navy-700 dark:text-white">
                                        Drop your files here, or{" "}
                                        <span className="font-bold text-brand-500 dark:text-brand-400">
                                            browse
                                        </span>
                                    </p>
                                    <p className="pt-2 text-sm text-gray-600">
                                        PNG, JPG and GIF files are allowed
                                    </p>
                                </div>
                            }
                            formData={formData}
                            setFormData={field.setFormData}
                            handleImageUpload={(file) => {
                                field.setFormData({ ...formData, picture: file });
                            }}
                            disabled={isDisabled} // Add the disabled prop here
                        />
                    </FormControl>
                </Grid>
            );
        case "tel": // Add a new case for "tel" type
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <MuiTelInput
                            id={field.id}
                            value={getNestedValue(formData, field.id) || ""}
                            label={field.label}
                            onChange={(value) => {
                                field.handleInputChange({ target: { id: field.id, name: field.id, value } });
                            }
                            }
                            error={getNestedValue(formData, field.id) || "" && !matchIsValidTel(getNestedValue(formData, field.id) || "")}
                            helperText={getNestedValue(formData, field.id) || "" && !matchIsValidTel(getNestedValue(formData, field.id) || "") && 'Invalid phone number'}
                            disabled={isDisabled} // Add the disabled prop here
                        />
                    </FormControl>
                </Grid>
            );
        case "cnpj":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <TextField
                            key={field.id}
                            label={field.label}
                            placeholder="00.000.000/0000-00"
                            id={field.id}
                            name={field.id}
                            type="text"
                            value={getNestedValue(formData, field.id) || "" || ""}
                            onChange={field.handleInputChange}
                            disabled={isDisabled}
                            InputProps={{
                                inputComponent: CNPJMask,
                            }}
                        />
                    </FormControl>
                </Grid>
            );
        case "percentage":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <TextField
                            inputRef={inputRef}
                            key={field.id}
                            sx={field.sx}
                            label={field.label}
                            placeholder={field.placeholder}
                            id={field.id}
                            name={field.id}
                            type="text"
                            // Show raw input during editing, formatted value when not editing
                            value={percentageState.isEditing
                                ? percentageState.editValue
                                : decimalToPercentage(getNestedValue(formData, field.id))}
                            onFocus={() => {
                                // When focused, switch to editing mode and prepare the edit value
                                const decimal = getNestedValue(formData, field.id);
                                const percentValue = decimal !== null && decimal !== undefined && decimal !== ''
                                    ? decimalToPercentage(decimal)
                                    : '';

                                setPercentageState({
                                    isEditing: true,
                                    editValue: percentValue
                                });

                                // Select all text for easy replacement
                                setTimeout(() => {
                                    if (inputRef.current) {
                                        inputRef.current.select();
                                    }
                                }, 0);
                            }}
                            onChange={(e) => {
                                const inputValue = e.target.value;

                                // Validate input to only allow numbers and a single decimal point.
                                // When allowNegative is enabled, also permit an optional leading minus.
                                const validationRegex = field.allowNegative
                                    ? /^-?[0-9]*\.?[0-9]*$/
                                    : /^[0-9]*\.?[0-9]*$/;
                                if (!validationRegex.test(inputValue) && inputValue !== '') {
                                    return;
                                }

                                // Update local edit state without converting to decimal yet
                                setPercentageState(prev => ({
                                    ...prev,
                                    editValue: inputValue
                                }));
                            }}
                            onBlur={() => {
                                // When user leaves the field, exit editing mode and update form data
                                setPercentageState(prev => ({
                                    ...prev,
                                    isEditing: false
                                }));

                                // Only update the form data if we have a valid, parseable input.
                                // Guard against mid-typing values like "-" or "." which parseFloat → NaN.
                                const raw = percentageState.editValue;
                                if (raw !== '' && !Number.isNaN(parseFloat(raw))) {
                                    const decimalValue = percentageToDecimal(raw);

                                    field.handleInputChange({
                                        target: {
                                            id: field.id,
                                            name: field.id,
                                            value: decimalValue
                                        }
                                    });
                                }
                            }}
                            // Handle Enter key to confirm input
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    inputRef.current.blur();
                                }
                            }}
                            disabled={isDisabled}
                            helperText={field.helperText}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                inputProps: {
                                    // Only clamp at 0 when negatives are not allowed.
                                    ...(field.allowNegative ? {} : { min: 0 }),
                                    step: field.inputProps?.step || 0.01,
                                    ...field.inputProps
                                }
                            }}
                        />
                    </FormControl>
                </Grid>
            );
        case "file":
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense' error={!!field.error}>
                        <Button
                            variant="outlined"
                            component="label"
                            sx={{
                                height: 56,
                                justifyContent: 'flex-start',
                                borderColor: field.error ? 'error.main' : 'divider',
                                textAlign: 'left'
                            }}
                            disabled={isDisabled}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MdOutlineCloudUpload style={{ fontSize: '24px' }} />
                                {field.value ? field.value.name : field.label || 'Choose File'}
                            </Box>
                            <input
                                type="file"
                                hidden
                                accept={field.accept || '*'}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    field.handleInputChange({
                                        target: {
                                            id: field.id,
                                            name: field.id,
                                            value: file || null
                                        }
                                    });
                                }}
                            />
                        </Button>
                        {field.error && (
                            <FormHelperText error>{field.error}</FormHelperText>
                        )}
                        {field.value && !field.error && (
                            <FormHelperText>
                                Selected: {field.value.name} ({(field.value.size / 1024 / 1024).toFixed(2)} MB)
                            </FormHelperText>
                        )}
                        {field.currentFile && !field.value && (
                            <FormHelperText>
                                Current file: <a href={field.currentFile} target="_blank" rel="noopener noreferrer">View</a>
                            </FormHelperText>
                        )}
                        {field.helperText && (
                            <FormHelperText>{field.helperText}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
            );
        default:
            return (
                <Grid xs={12} md={mdColSpan} key={field.id}>
                    <FormControl fullWidth margin='dense'>
                        <TextField
                            key={field.id}
                            label={field.label}
                            placeholder={field.placeholder}
                            id={field.id}
                            name={field.id}
                            type={field.type}
                            value={getNestedValue(formData, field.id) || "" || ""}
                            onChange={field.handleInputChange}
                            disabled={isDisabled} // Add the disabled prop here
                            helperText={field.helperText}
                            inputProps={field.inputProps}
                        />
                    </FormControl>
                </Grid>
            );
    }
};


export default DynamicFormField;
